/** Canvas confetti — side cannons, ribbons, bursts. No npm. */

const COLORS = [
  '#00FF85',
  '#6dffb3',
  '#ffffff',
  '#ffd54a',
  '#ff8a3d',
  '#ff5c8a',
  '#8b7cff',
  '#5ec8ff',
  '#ff6b4a',
];

function rand(a, b) {
  return a + Math.random() * (b - a);
}

function pick(arr) {
  return arr[(Math.random() * arr.length) | 0];
}

function makePiece(x, y, vx, vy) {
  const kind = Math.random() < 0.35 ? 'ribbon' : Math.random() < 0.55 ? 'rect' : 'circle';
  return {
    x,
    y,
    vx,
    vy,
    rot: rand(0, Math.PI * 2),
    vr: rand(-0.35, 0.35),
    w: kind === 'ribbon' ? rand(3, 5) : rand(5, 11),
    h: kind === 'ribbon' ? rand(14, 28) : kind === 'circle' ? rand(5, 9) : rand(7, 14),
    kind,
    color: pick(COLORS),
    gravity: rand(0.12, 0.22),
    drag: rand(0.985, 0.995),
    flutter: rand(0.4, 1.2),
    phase: rand(0, Math.PI * 2),
    opacity: 1,
  };
}

function drawPiece(ctx, p) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  // 3D flip — thickness fades as it turns
  const flip = Math.abs(Math.cos(p.rot * 2 + p.phase));
  ctx.globalAlpha = Math.max(0, p.opacity) * (0.55 + flip * 0.45);
  ctx.fillStyle = p.color;
  ctx.scale(1, 0.35 + flip * 0.65);

  if (p.kind === 'circle') {
    ctx.beginPath();
    ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
    ctx.fill();
  } else if (p.kind === 'ribbon') {
    ctx.beginPath();
    ctx.moveTo(-p.w / 2, -p.h / 2);
    ctx.quadraticCurveTo(p.w * 1.2, 0, -p.w / 2, p.h / 2);
    ctx.quadraticCurveTo(p.w * 0.4, 0, -p.w / 2, -p.h / 2);
    ctx.fill();
  } else {
    ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
  }
  ctx.restore();
}

/**
 * Celebrate burst from left/right cannons + center pop.
 * @param {object} opts
 * @param {number} [opts.durationMs=3200]
 * @param {number} [opts.particleCount=140]
 * @param {number} [opts.repeatEveryMs] — if set, re-burst on this interval until stopped
 * @param {number} [opts.maxMs] — total run time when repeating (default: durationMs if no repeat)
 */
export function fireConfetti({
  durationMs = 3200,
  particleCount = 140,
  repeatEveryMs,
  maxMs,
} = {}) {
  if (typeof document === 'undefined') return () => {};

  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999';
  document.body.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  const resize = () => {
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener('resize', resize);

  const W = () => window.innerWidth;
  const H = () => window.innerHeight;

  /** Side cannons + center fountain — feels like a real party popper */
  const burst = (n) => {
    const out = [];
    const left = Math.floor(n * 0.38);
    const right = Math.floor(n * 0.38);
    const mid = n - left - right;
    const cy = H() * 0.72;

    for (let i = 0; i < left; i += 1) {
      const angle = rand(-Math.PI * 0.55, -Math.PI * 0.12);
      const speed = rand(11, 22);
      out.push(
        makePiece(
          -8,
          cy + rand(-40, 40),
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        )
      );
    }
    for (let i = 0; i < right; i += 1) {
      const angle = rand(-Math.PI * 0.88, -Math.PI * 0.45);
      const speed = rand(11, 22);
      out.push(
        makePiece(
          W() + 8,
          cy + rand(-40, 40),
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        )
      );
    }
    for (let i = 0; i < mid; i += 1) {
      const angle = rand(-Math.PI * 0.85, -Math.PI * 0.15);
      const speed = rand(8, 18);
      out.push(
        makePiece(
          W() * 0.5 + rand(-60, 60),
          H() * 0.55 + rand(-20, 20),
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        )
      );
    }
    return out;
  };

  let pieces = burst(particleCount);
  const started = performance.now();
  let lastRepeat = started;
  const totalMs =
    maxMs != null
      ? maxMs
      : repeatEveryMs
        ? Math.max(durationMs, repeatEveryMs * 4)
        : durationMs;
  let raf = 0;
  let stopped = false;

  const tick = (now) => {
    if (stopped) return;
    const elapsed = now - started;

    if (repeatEveryMs && now - lastRepeat >= repeatEveryMs) {
      lastRepeat = now;
      pieces = pieces.concat(burst(Math.floor(particleCount * 0.85)));
      // Cap so mobile doesn't choke
      if (pieces.length > 420) pieces = pieces.slice(pieces.length - 420);
    }

    ctx.clearRect(0, 0, W(), H());

    const fadeStart = totalMs - 700;
    const globalFade =
      elapsed > fadeStart ? Math.max(0, 1 - (elapsed - fadeStart) / 700) : 1;

    pieces.forEach((p) => {
      p.vy += p.gravity;
      p.vx *= p.drag;
      p.vy *= p.drag;
      // Flutter sideways like real paper
      p.vx += Math.sin(now * 0.008 + p.phase) * p.flutter * 0.08;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr + Math.sin(now * 0.01 + p.phase) * 0.04;
      p.opacity = globalFade;
    });

    // Drop off-screen pieces
    pieces = pieces.filter((p) => p.y < H() + 40 && p.opacity > 0.02);

    pieces.forEach((p) => drawPiece(ctx, p));

    if (elapsed < totalMs) {
      raf = requestAnimationFrame(tick);
    } else {
      cleanup();
    }
  };

  const cleanup = () => {
    stopped = true;
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
  };

  raf = requestAnimationFrame(tick);
  return cleanup;
}

/** Finale / leaderboard — keeps popping every ~2s */
export function fireFinaleConfetti() {
  return fireConfetti({
    durationMs: 10000,
    maxMs: 10000,
    particleCount: 160,
    repeatEveryMs: 2000,
  });
}
